import { Line, Text } from "@react-three/drei";
import { useMemo, useState, useEffect, useRef } from "react";
import { calculateFontSize, createRectanglePoints } from "../utils";
import { groupBinsByRows } from "../../../utils/visualizerUtils";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import React from "react";

// Module-level variable to track the currently selected rack ID
let selectedRackId = null;
const rackListeners = new Set();

// Function to update selection and notify all racks
const setSelectedRack = (rackId) => {
  const previousSelected = selectedRackId;
  selectedRackId = rackId;

  // Notify all listeners about the change
  rackListeners.forEach((listener) => listener(rackId, previousSelected));
};

function Rack({ obj, warehouseInfo }) {
  const [isSelected, setIsSelected] = useState(false);
  const rows = groupBinsByRows(obj.bins || []);
  const scale = 1;

  const { camera, scene, raycaster } = useThree();

  // Calculate dynamic rack height based on bins
  const calculateRackHeight = () => {
    if (!obj.bins || obj.bins.length === 0) {
      // Default height when no bins
      return 5.8;
    }

    // Get the height from the first bin (assuming all bins have same height)
    const binHeight = (obj.bins[0]?.size?.[1] || 1.8) * scale; // Default to 1.8 if not available

    // Calculate total rack height: number of rows * (bin height + shelf thickness + gap)
    const shelfThickness = 0.02 * scale; // Thickness of each shelf/level
    const verticalGap = 0.15 * scale; // Gap between bin top and next shelf
    const baseHeight = 0.1 * scale; // Base platform height
    const topFrameHeight = 0.08 * scale; // Top frame height

    const totalBinHeight = rows.length * binHeight;
    const totalShelfHeight = (rows.length + 1) * shelfThickness; // +1 for top shelf
    const totalGapHeight = rows.length * verticalGap;

    return baseHeight + totalBinHeight + totalShelfHeight + totalGapHeight + topFrameHeight;
  };

  const poleHeight = calculateRackHeight();
  const topFramePosition = poleHeight;

  // Calculate level positions for shelves
  const calculateLevelPositions = () => {
    if (!obj.bins || obj.bins.length === 0) {
      const defaultGap = 1.8;
      return [
        { height: 0.1, isBottom: true }, // Bottom shelf
        { height: 0.1 + defaultGap, isBottom: false },
        { height: 0.1 + defaultGap * 2, isBottom: false },
      ];
    }

    const binHeight = obj.bins[0]?.size?.[1] || 1.8;
    const shelfThickness = 0.02;
    const verticalGap = 0.15;
    const baseHeight = 0.1;

    const levelPositions = [];

    // Bottom shelf (at base)
    levelPositions.push({
      height: baseHeight,
      isBottom: true,
    });

    // Calculate shelf positions for each row
    rows.forEach((_, index) => {
      if (index === rows.length - 1) return;
      const shelfPosition = baseHeight + index * (binHeight + verticalGap + shelfThickness) + binHeight + verticalGap;

      levelPositions.push({
        height: shelfPosition,
        isBottom: false,
      });
    });

    return levelPositions;
  };

  const levelPositions = calculateLevelPositions();

  // Subscribe to global selection changes
  useEffect(() => {
    const handleSelectionChange = (newSelectedId, previousSelectedId) => {
      // If this rack was previously selected but now another rack is selected
      if (previousSelectedId === obj.id && newSelectedId !== obj.id) {
        setIsSelected(false);
      }
      // If this rack is now selected
      if (newSelectedId === obj.id) {
        setIsSelected(true);
      }
    };

    // Add this rack to listeners
    rackListeners.add(handleSelectionChange);

    // Cleanup on unmount
    return () => {
      rackListeners.delete(handleSelectionChange);
    };
  }, [obj.id]);

  // Add padding constants
  const padding = 0.1; // 10cm padding on all sides

  const rackWidth = obj?.x2 - obj?.x1;
  const rackHeight = obj?.y2 - obj?.y1;

  const isHorizontal = rackWidth > rackHeight;
  const textRotation = isHorizontal ? [0, 0, 0] : [0, 0, Math.PI / 2];
  const points = useMemo(() => createRectanglePoints(obj.size[0], obj.size[2]), [obj.size]);
  const rotationY = obj.rotation ? (-obj.rotation * Math.PI) / 180 : 0;

  const fontSize = useMemo(() => {
    return isHorizontal ? calculateFontSize(obj.size[0], obj.size[2], obj.name) - 0.1 : calculateFontSize(obj.size[2], obj.size[0], obj.name);
  }, [isHorizontal, obj.size, obj.name]);

  // Pole dimensions
  const poleRadius = 0.05;

  const halfWidth = obj.size[0] / 2;
  const halfLength = obj.size[2] / 2;

  // Adjust dimensions with padding
  const paddedWidth = obj.size[0] + padding * 2;
  const paddedLength = obj.size[2] + padding * 2;
  const paddedHalfWidth = paddedWidth / 2;
  const paddedHalfLength = paddedLength / 2;

  // Update corner positions with padding
  const cornerPositions = [
    [-paddedHalfWidth + poleRadius, 0, -paddedHalfLength + poleRadius],
    [paddedHalfWidth - poleRadius, 0, -paddedHalfLength + poleRadius],
    [-paddedHalfWidth + poleRadius, 0, paddedHalfLength - poleRadius],
    [paddedHalfWidth - poleRadius, 0, paddedHalfLength - poleRadius],
  ];

  // Define the four sides with their corner pairs
  const sides = [
    {
      name: "front",
      corners: [cornerPositions[0], cornerPositions[1]], // front-left to front-right
      vertical: true,
    },
    {
      name: "back",
      corners: [cornerPositions[2], cornerPositions[3]], // back-left to back-right
      vertical: true,
    },
  ];

  // Create multiple Z-braces for each side - FIXED to respect top frame
  const createMultipleZBracing = (side, heightOffset = 0.1) => {
    const [cornerA, cornerB] = side.corners;
    const [x1, y1, z1] = cornerA;
    const [x2, y2, z2] = cornerB;

    const isVerticalSide = side.vertical;
    const braces = [];

    // Calculate brace levels dynamically based on poleHeight, ensuring they don't exceed top frame
    const braceLevels = [];
    const numBraces = 3; // Number of Z-braces per side

    // Available height for bracing (from base to just below top frame)
    const availableBracingHeight = poleHeight - heightOffset - 0.2; // Leave space at top

    for (let i = 0; i < numBraces; i++) {
      // Distribute braces evenly within available height
      const baseHeight = heightOffset + (i * availableBracingHeight) / numBraces;
      const reverse = i % 2 === 1; // Alternate Z orientation

      // Ensure Z-height doesn't exceed available space
      const maxZHeight = availableBracingHeight / numBraces;
      const zHeight = Math.min(maxZHeight, 0.8); // Cap at 0.8 or available space

      // Ensure the top of the Z doesn't exceed the top frame
      const actualTopHeight = baseHeight + zHeight;
      if (actualTopHeight <= poleHeight - 0.1) {
        // Leave some clearance below top frame
        braceLevels.push({
          baseHeight,
          zHeight,
          reverse,
        });
      }
    }

    braceLevels.forEach((level, index) => {
      const { baseHeight, zHeight, reverse } = level;

      // Bottom points
      const bottomLeft = [x1, baseHeight, z1];
      const bottomRight = [x2, baseHeight, z2];

      // Top points - ensure they don't exceed top frame
      const topLeft = [x1, baseHeight + zHeight, z1];
      const topRight = [x2, baseHeight + zHeight, z2];

      // Middle points for Z shape
      let middlePoint1;

      if (isVerticalSide) {
        // For front/back sides, middle points move in X direction
        if (reverse) {
          // Reversed Z: top-left to middle-right, middle-right to bottom-left
          middlePoint1 = [x2, baseHeight + zHeight / 2, z1];
          braces.push([topLeft, middlePoint1], [middlePoint1, bottomRight]);
        } else {
          // Normal Z: bottom-left to middle-right, middle-right to top-left
          middlePoint1 = [x2, baseHeight + zHeight / 2, z1];
          braces.push([bottomLeft, middlePoint1], [middlePoint1, topRight]);
        }
      } else {
        // For left/right sides, middle points move in Z direction
        if (reverse) {
          // Reversed Z
          middlePoint1 = [x1, baseHeight + zHeight / 2, z2];
          braces.push([topLeft, middlePoint1], [middlePoint1, bottomRight]);
        } else {
          // Normal Z
          middlePoint1 = [x1, baseHeight + zHeight / 2, z2];
          braces.push([bottomLeft, middlePoint1], [middlePoint1, topRight]);
        }
      }
    });

    return braces;
  };

  // Handle click on any part of the rack
  const handleRackClick = (event) => {
    console.log("handleRackClick");

    // event.stopPropagation(); // Prevent event bubbling
    // if (isSelected) {
    //   // If already selected, deselect it
    //   setSelectedRack(null);
    // } else {
    //   // If not selected, select this rack (will automatically deselect others)
    //   setSelectedRack(obj.id);
    // }
  };

  const textRef = useRef();

  useFrame(() => {
    if (textRef.current) {
      textRef.current.lookAt(camera.position);
    }
  });

  return (
    <group rotation={[0, rotationY, 0]} castShadow receiveShadow>
      {/* Clickable wrapper for the entire rack */}
      <group onClick={handleRackClick}>
        {/* Highlight overlay - appears when rack is selected */}
        {isSelected && (
          <group>
            {/* Highlight for base platform */}
            <mesh position={[0, 0.06, 0]} rotation={[-Math.PI / 2, 0, 0]}>
              <boxGeometry args={[paddedWidth + 0.02, paddedLength + 0.02, 0.12]} />
              <meshBasicMaterial color="#eb1818" transparent opacity={0.1} />
            </mesh>

            {/* Highlight for top frame */}
            <mesh position={[0, topFramePosition + 0.04, 0]} rotation={[-Math.PI / 2, 0, 0]}>
              <boxGeometry args={[paddedWidth + 0.02, paddedLength + 0.02, 0.1]} />
              <meshBasicMaterial color="#eb1818" transparent opacity={0.1} />
            </mesh>

            {/* Highlight for poles */}
            {cornerPositions.map((position, index) => (
              <mesh key={`highlight-pole-${index}`} position={[position[0], poleHeight / 2, position[2]]}>
                <cylinderGeometry args={[poleRadius + 0.01, poleRadius + 0.01, poleHeight + 0.1, 8]} />
                <meshBasicMaterial color="#eb1818" transparent opacity={0.1} />
              </mesh>
            ))}

            {/* Highlight for transparent sides */}
            {/* Front highlight */}
            <mesh position={[0, poleHeight / 2, -paddedHalfLength]} rotation={[0, 0, 0]}>
              <boxGeometry args={[paddedWidth + 0.02, poleHeight + 0.1, 0.02]} />
              <meshBasicMaterial color="#eb1818" transparent opacity={0.1} />
            </mesh>

            {/* Back highlight */}
            <mesh position={[0, poleHeight / 2, paddedHalfLength]} rotation={[0, 0, 0]}>
              <boxGeometry args={[paddedWidth + 0.02, poleHeight + 0.1, 0.02]} />
              <meshBasicMaterial color="#eb1818" transparent opacity={0.1} />
            </mesh>

            {/* Left highlight */}
            <mesh position={[-paddedHalfWidth, poleHeight / 2, 0]} rotation={[0, 0, 0]}>
              <boxGeometry args={[0.02, poleHeight + 0.1, paddedLength + 0.02]} />
              <meshBasicMaterial color="#eb1818" transparent opacity={0.1} />
            </mesh>

            {/* Right highlight */}
            <mesh position={[paddedHalfWidth, poleHeight / 2, 0]} rotation={[0, 0, 0]}>
              <boxGeometry args={[0.02, poleHeight + 0.1, paddedLength + 0.02]} />
              <meshBasicMaterial color="#eb1818" transparent opacity={0.1} />
            </mesh>

            {/* Highlight for Z-bracing */}
            {sides.map((side, sideIndex) =>
              createMultipleZBracing(side, 0.05).map((bracePoints, braceIndex) => (
                <Line key={`highlight-brace-${sideIndex}-${braceIndex}`} points={bracePoints} color="#eb1818" lineWidth={3} transparent opacity={0.5} />
              ))
            )}
          </group>
        )}

        {/* Beveled Platform Base */}
        <group position={[0, 0.05, 0]}>
          {/* Main platform with beveled edges */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
            <boxGeometry args={[paddedWidth, paddedLength, 0.1]} />
            <meshStandardMaterial color={"#c7cbcd"} metalness={0.2} roughness={0.7} />
          </mesh>

          {/* Side meshes for darker edges */}
          {/* Front side */}
          <mesh position={[0, 0, -paddedLength / 2]} rotation={[0, 0, 0]}>
            <boxGeometry args={[paddedWidth, 0.1, 0.02]} />
            <meshStandardMaterial color="#9a9ea0" metalness={0.3} roughness={0.8} />
          </mesh>

          {/* Back side */}
          <mesh position={[0, 0, paddedLength / 2]} rotation={[0, 0, 0]}>
            <boxGeometry args={[paddedWidth, 0.1, 0.02]} />
            <meshStandardMaterial color="#9a9ea0" metalness={0.3} roughness={0.8} />
          </mesh>

          {/* Left side */}
          <mesh position={[-paddedWidth / 2, 0, 0]} rotation={[0, 0, 0]}>
            <boxGeometry args={[0.02, 0.1, paddedLength]} />
            <meshStandardMaterial color="#9a9ea0" metalness={0.3} roughness={0.8} />
          </mesh>

          {/* Right side */}
          <mesh position={[paddedWidth / 2, 0, 0]} rotation={[0, 0, 0]}>
            <boxGeometry args={[0.02, 0.1, paddedLength]} />
            <meshStandardMaterial color="#9a9ea0" metalness={0.3} roughness={0.8} />
          </mesh>

          {/* Corner mounting brackets */}
          {cornerPositions.map((position, index) => (
            <group key={`base-bracket-${index}`} position={[position[0], 0.02, position[2]]}>
              <mesh rotation={[-Math.PI / 2, 0, 0]}>
                <boxGeometry args={[0.1, 0.1, 0.02]} />
                <meshStandardMaterial color="#666666" metalness={0.6} />
              </mesh>
              {/* Bolt details */}
              <mesh position={[0, 0.011, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                <cylinderGeometry args={[0.015, 0.015, 0.01, 8]} />
                <meshStandardMaterial color="#888888" metalness={0.8} />
              </mesh>
            </group>
          ))}

          {/* Label on raised platform */}
          <Text
            ref={textRef}
            position={[0, topFramePosition + 0.1, obj.size[1] / 2 + 0.1]}
            lineHeight={1}
            letterSpacing={0.02}
            anchorX="center"
            anchorY="middle"
            fontSize={fontSize}
            color="#222"
            fontWeight={"bold"}
          >
            {obj.name}
          </Text>
        </group>

        {/* Poles */}
        {cornerPositions.map((position, index) => (
          <mesh key={`pole-${index}`} position={[position[0], poleHeight / 2, position[2]]}>
            <cylinderGeometry args={[poleRadius, poleRadius, poleHeight, 8]} />
            <meshBasicMaterial color="#002d82" />
          </mesh>
        ))}

        {/* Top rods connecting left and right poles */}
        <>
          {/* Left side rod (connect front-left → back-left) */}
          <mesh
            position={[-paddedWidth / 2, topFramePosition - 0.2, 0]} // X = left pole, Y = top height, Z = center
            rotation={[Math.PI / 2, 0, 0]} // rotate cylinder from Y → Z axis
          >
            <cylinderGeometry args={[0.03, 0.03, paddedLength, 16]} />
            <meshStandardMaterial color="#002d82" metalness={0.8} roughness={0.3} />
          </mesh>

          {/* Right side rod (connect front-right → back-right) */}
          <mesh
            position={[paddedWidth / 2, topFramePosition - 0.2, 0]} // X = right pole
            rotation={[Math.PI / 2, 0, 0]} // rotate cylinder from Y → Z axis
          >
            <cylinderGeometry args={[0.03, 0.03, paddedLength, 16]} />
            <meshStandardMaterial color="#002d82" metalness={0.8} roughness={0.3} />
          </mesh>
        </>

        {/* Multiple Z-bracing on all 4 sides - NOW PROPERLY BOUNDED */}
        {sides.map((side, sideIndex) =>
          createMultipleZBracing(side).map((bracePoints, braceIndex) => (
            <Line key={`z-brace-${sideIndex}-${braceIndex}`} points={bracePoints} color="#002d82" lineWidth={1.5} />
          ))
        )}

        {/* SHELVES/LEVELS - Realistic industrial shelves */}
        {levelPositions.map((level, index) => (
          <group key={`shelf-${index}`}>
            {/* Main shelf surface */}
            <mesh position={[0, level.height, 0]}>
              <boxGeometry
                args={[
                  (paddedWidth - 0.1) * scale, // width
                  0.06, // thickness of the shelf (increase this for more thickness)
                  (paddedLength - 0.1) * scale, // depth
                ]}
              />
              <meshStandardMaterial color="#6e6d6d" metalness={0.3} roughness={0.7} />
            </mesh>

            {/* Shelf edge/frame for thickness */}
            <mesh position={[0, level.height + 0.01 * scale, 0]} rotation={[-Math.PI / 2, 0, 0]}>
              <ringGeometry args={[(Math.min(paddedWidth, paddedLength) / 2 - 0.06) * scale, (Math.min(paddedWidth, paddedLength) / 2 - 0.05) * scale, 32]} />
              <meshStandardMaterial color="#c91414" metalness={0.4} />
            </mesh>

            {/* Shelf supports at corners */}
            {cornerPositions.map((position, cornerIndex) => (
              <mesh
                key={`shelf-support-${index}-${cornerIndex}`}
                position={[position[0] * scale, level.height + 0.005 * scale, position[2] * scale]}
                rotation={[-Math.PI / 2, 0, 0]}
              >
                <boxGeometry args={[0.05, 0.05, 0.01].map((v) => v * scale)} />
                <meshStandardMaterial color="#777777" metalness={0.5} />
              </mesh>
            ))}
          </group>
        ))}
      </group>
    </group>
  );
}

export default React.memo(Rack); // Use React.memo to memoize the component and prevent unnecessary re-renders;
